import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { AlertValue, alertOpen } from "../../features/alert.slice";
import { ConstMessage } from "../../config/const";
import { ChevronLeftIcon } from "@heroicons/react/24/solid";
import { Spiner } from "../../components/spinner/Spiner";
import { convertDateToJapaneseString } from "../../utility/ConvertUtily";
import Badge from "../../components/badges/Badges";
import TableList, { GridCol, GridRow } from "../../components/table/TableList";
import Button from "../../components/button/Button";
import { PointAdjustmentDataExportModal } from "./PointAdjustmentDataExportModal";
import pointDataQuery from '../../firestore/PointData.query';
import { PointAdjustHistory, AdjustUsers, newPointAdjustHistory, getExpirationDate } from "../../model/PointData";
import { auth } from "../../firebase";
import { Progress } from "../../components/progress/Progress";
import { Company } from "../../model/Companies";
import companiesQuery from "../../firestore/Companies.query";
import { checkOption } from "../../controller/OptionController";
import { Timestamp } from "firebase/firestore";
import { requestGetAdjustUsers } from "../../api/Point.api";

export const PointAdjustmentInfo = () => {

  // ポイント履歴ID
  const { pointAdjustmentId } = useParams<{ pointAdjustmentId: string }>();

  const dispatch = useDispatch();
  const navigate = useNavigate();
  // ログイン中の団体管理者情報
  const currentUser = auth.currentUser;
  
  const [isLoad, setIsLoad] = useState(true);
  const [pointAdjustment, setPointAdjustment] = useState<PointAdjustHistory>(newPointAdjustHistory);
  const [adjustUsersList, setAdjustUsersList] = useState<AdjustUsers[]>([]);
  const [selectNumber, setSelectNumber] = useState(50);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [loadProgress, setLoadProgress] = useState<number>(-1);
  const [company, setCompany] = useState<Company>();
  const [firstPageFlg, setFirstPageFlg] = useState(false);
  const [nowPage, setNowPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const styles = {
    header: "text-right mr-8 text-sm text-gray-500 whitespace-nowrap",
    body:"text-left text-sm",
    margin:"flex ml-6 mb-3",
  };

  useEffect(() => {
    const f = async () => {
      try {
        if(pointAdjustmentId){
          // 団体を取得
          const company = await companiesQuery.getCompany(currentUser?.tenantId!);
          setCompany(company);
          // ポイントカテゴリ設定一覧の取得
          const pointCategorySettingList = await pointDataQuery.getPtCategorySettingsList(currentUser?.tenantId!);
          pointCategorySettingList.map((data) => {
            const expiryMap = data.dateOfExpiryMap;
            if (expiryMap && expiryMap.expirationYm instanceof Timestamp) {
              expiryMap.expirationYm = expiryMap.expirationYm.toDate();
            }
          });
          // ポイント調整履歴のデータを取得
          const pointAdjustmentData = await pointDataQuery.getPointAdjustHistory(currentUser?.tenantId!, pointAdjustmentId);
          if(pointCategorySettingList){
            const categorySetting = pointCategorySettingList.filter((data) => data.categoryId === pointAdjustmentData.ptCategoryId)[0];
            if(pointAdjustmentData.grantPoints && pointAdjustmentData.grantPoints > 0){
              pointAdjustmentData.expirationDate = getExpirationDate(categorySetting, pointAdjustmentData.occurrenceDate);
            }
          }
          setPointAdjustment(pointAdjustmentData);
        }
      } catch (error) {
        console.error(error);
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILED_GET_DATA, "danger")));
      }
      setIsLoad(false);
    };
    f();
  }, [currentUser?.tenantId]);

  // 調整対象者を取得する共通処理
  useEffect(() => {
    const fetchPointsByPage = async () => {
      try {
        if (!pointAdjustmentId) return;
        const result = await requestGetAdjustUsers(pointAdjustmentId, nowPage, selectNumber);
        if (result.success) {
          setAdjustUsersList(result.adjustUsers);
          setTotalCount(result.totalCount);
        } else {
          console.error("調整対象者取得失敗:", result.error);
          dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILED_GET_DATA, "danger")));
        }
      } catch (err) {
        console.error("調整対象者取得失敗:", err);
        dispatch(alertOpen(AlertValue(ConstMessage.ERR_FAILED_GET_DATA, "danger")));
      } finally {
        setIsLoad(false);
      }
    };
    fetchPointsByPage();
  }, [nowPage, selectNumber, dispatch]);

  // 表示件数変更時に1ページ目を表示する
  useEffect(() => {
    setFirstPageFlg(true)
  },[selectNumber]);

  // ページング処理
  const pagingFunc = useCallback((page: number) => {
    setFirstPageFlg(false);
    setNowPage(page);
  }, []);

  const selectNumberList = [
    { id: 50, name: "50" },
    { id: 100, name: "100" },
    { id: 200, name: "200" },
  ];

  const columns: GridCol[] = [
    {
      field: "userId",
      headerName: "利用者番号",
      style: "w-1/6",
      sort: false
    },
    {
      field: "userName",
      headerName: "名前",
      style: "w-1/6",
      sort: false
    },
    {
      field: "groupNameArr",
      headerName: "所属グループ",
      style: "w-2/6",
      sort: false
    },
    {
      field: "executionResult",
      headerName: "実行結果",
      style: "w-1/6",
      sort: false
    },
    {
      field: "errorDetail",
      headerName: "",
      style: "w-1/6",
      sort: false
    },
  ];

  const rowList: GridRow[] = adjustUsersList.map<GridRow>((item) => {
    const userGroupList = item.groupNameArr && item.groupNameArr.length > 0 ? item.groupNameArr.map(
      (item: any) => (
        <Badge
          color="blue"
        >
          {item}
        </Badge>
      )) : "";
    return{
      rowData: {
        userId: item.displayUserId,
        userName: item.userName,
        groupNameArr: userGroupList,
        executionResult: (<div className="text-left ml-[22px]">{item.executionResult ? "○" : "✕"}</div>),
        errorDetail: item.errorDetail ?? "",
      },
      style: 'sm:whitespace-normal'
    };
  });

  return(
    <div className="flex h-full">
      <div className="relative z-0 flex flex-1">
        <main className="relative z-0 flex-1 focus:outline-none order-last">
          <nav
            className="flex items-start px-4 py-3"
            aria-label="Breadcrumb"
          >
            <div className="inline-flex items-center space-x-3 text-sm font-medium text-gray-900 cursor-pointer">
              <span onClick={() => navigate("/PointAdjustmentHistory")} className="flex items-center">
                <ChevronLeftIcon className="-ml-2 h-5 w-5 text-gray-400 mr-3" />一覧へ
              </span>
            </div>
          </nav>
          <Progress
            subText={ConstMessage.PROGRESS_SUB_TEXT}
            open={loadProgress >= 0 && loadProgress < 100}
            progressNum={loadProgress}
            color={"green"}
          />
          {isDialogOpen && (
            <PointAdjustmentDataExportModal
              children
              open={isDialogOpen}
              toggle={() => {setIsDialogOpen(!isDialogOpen)}}
              closeOnClickOutside={false}
              setLoadProgress={setLoadProgress}
              pointAdjustment={pointAdjustment}
              adjustUsersList={adjustUsersList}
              company= {company}
            />
          )}
          {isLoad ? <Spiner/> : (
            <>
              <div className="mt-2 mb-4 mx-5">
                <div className="flex pl-7 pr-2 justify-between">
                  <div className="flex justify-start items-center">
                    <h2 className="text-xl font-semibold text-gray-900 mr-10">
                      ポイント調整履歴詳細
                    </h2>
                  </div>
                  <div className="flex justify-end">
                    <Button
                      size={"md"}
                      className="h-8 items-center text-sm"
                      onClick={async () => {
                        if (await checkOption("point", dispatch)) {
                          setIsDialogOpen(!isDialogOpen);
                          }
                        }
                      }
                      children={"ポイント調整情報出力"}
                    />
                  </div>
                </div>
                <div className="bg-indigo-50 mt-3 mx-2 py-8 min-w-[700px]">
                  <div className={`${styles.margin}`}>
                    <div className={`${styles.header}`}>　　　　登録日</div>
                    <div className={`${styles.body}`}>{convertDateToJapaneseString(pointAdjustment.createAt)}</div>
                    <div className={`${styles.header} ml-24`}>登録者</div>
                    <div className={`${styles.body}`}>{pointAdjustment.createUser}</div>
                  </div>
                  <div className={`${styles.margin}`}>
                    <div className={`${styles.header}`}>　　　カテゴリ</div>
                    <div className={`${styles.body}`}>{pointAdjustment.ptCategoryName}</div>
                  </div>
                  <div className={`${styles.margin}`}>
                    <div className={`${styles.header}`}>　　　　タイプ</div>
                    <div className={`${styles.body}`}>{pointAdjustment.ptTypeName}</div>
                  </div>
                  <div className={`${styles.margin}`}>
                    <div className={`${styles.header}`}>　　ポイント名</div>
                    <div className={`${styles.body}`}>{pointAdjustment.ptName}</div>
                  </div>
                  <div className={`${styles.margin}`}>
                    <div className={`${styles.header}`}>　　　有効期限</div>
                    <div className={`${styles.body}`}>{pointAdjustment.expirationDate? convertDateToJapaneseString(pointAdjustment.expirationDate) : ""}</div>
                  </div>
                  <div className={`${styles.margin}`}>
                    <div className={`${styles.header}`}>ポイント発生日</div>
                    <div className={`${styles.body}`}>{convertDateToJapaneseString(pointAdjustment.occurrenceDate)}</div>
                  </div>
                  <div className={`${styles.margin}`}>
                    <div className={`${styles.header}`}>　　ポイント数</div>
                    <div className={`${styles.body}`}>{pointAdjustment.grantPoints}</div>
                  </div>
                  <div className={`${styles.margin}`}>
                    <div className={`${styles.header}`}>　　　　　備考</div>
                    <div className={`${styles.body} whitespace-pre-wrap`}>{pointAdjustment.note}</div>
                  </div>
                </div>
              </div>
              <div>
                <div className={"px-6 pb-2"}>
                  <TableList
                    header
                    gridCol={columns}
                    gridRow={rowList}
                    gridRowLength={totalCount}
                    setSelectNumber={setSelectNumber}
                    selectNumberList={selectNumberList}
                    limit={selectNumber}
                    defaultSortKey={columns[0].field}
                    setFirstPageFlg={setFirstPageFlg}
                    firstPageFlg={firstPageFlg}
                    pagingFunc={pagingFunc}
                  />
                </div>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}